import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const EASE = [0.16, 1, 0.3, 1];

function MaskLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero({ heroImage }) {
  return (
    <header className="relative min-h-[88vh] flex flex-col justify-between max-w-[1400px] mx-auto px-5 md:px-10 pt-10 pb-8 z-10" data-testid="hero">
      <div className="flex items-start justify-between">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[#1C2430]/60"
        >
          AWS Certified · Machine Learning Engineer
          <br />Associate — MLA-C01
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[#1C2430]/60 text-right hidden sm:block"
        >
          A complete study guide
          <br />Est. from one real pass
        </motion.div>
      </div>

      <div className="relative flex-1 flex items-center py-4">
        {heroImage && (
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: EASE, delay: 0.4 }}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[38%] max-w-[420px] aspect-[3/4] hidden lg:block overflow-hidden border border-[var(--border-heavy)]"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
          >
            <img
              src={heroImage}
              alt="Abstract"
              className="w-full h-full object-cover"
              style={{ filter: "grayscale(1) contrast(1.05)", mixBlendMode: "multiply" }}
            />
          </motion.div>
        )}
        <h1 className="font-serif-display font-light leading-[0.86] tracking-tight text-[#1C2430] text-[15vw] md:text-[11vw] lg:text-[8.5rem] relative z-10" style={{ fontVariantNumeric: "lining-nums", fontFeatureSettings: '"lnum" 1' }}>
          <MaskLine delay={0.35}>The MLA-C01</MaskLine>
          <MaskLine delay={0.48}>
            <span className="italic text-[#2F6F4F]">From-Zero</span>
          </MaskLine>
          <MaskLine delay={0.61}>Study Guide</MaskLine>
        </h1>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: EASE, delay: 0.9 }}
        className="max-w-2xl"
      >
        <p className="text-[16px] leading-relaxed text-[#1C2430]/75">
          A complete, plain-English study guide for the AWS Certified Machine Learning
          Engineer — Associate exam. Assumes you know nothing. This is the exact content I
          read in my <span className="font-semibold text-[#2F6F4F]">first week</span> (no courses, no videos, no bootcamp),
          learning the hard way what the exam actually tests and how to remember it. It aims
          to cover everything, but it's one person's notes, so there may still be a few gaps.
        </p>
        <Link
          to="/learn/c0"
          className="inline-flex items-center gap-2 mt-6 font-mono-ui text-[11px] uppercase tracking-[0.18em] bg-[#1C2430] text-[#FBFBF9] px-5 py-3 hover:bg-[#364152] transition-colors"
          data-testid="hero-start-link"
        >
          Begin reading →
        </Link>
      </motion.div>
    </header>
  );
}
